import React from 'react';
import { Card, Typography, Button, Tag, Space, Avatar, Divider } from 'antd';
import { useNavigate } from 'react-router-dom';
import { 
    EyeOutlined,
    FileTextOutlined,
    DollarCircleOutlined,
    ShoppingCartOutlined,
    ShopOutlined,
    UserOutlined,
    HeartOutlined,
    HeartFilled,
    StarFilled
} from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { message } from 'antd';
import { toggleFavoriteLot } from '../../redux/userSlice';
import favoriteService from '../../services/favoriteService';
import env from '../../env';

const { Title, Text } = Typography;

// Helper functions
const formatCurrency = (price) => {
    if (price === null || price === undefined) return 'PKR 0.00';
    return `PKR ${Number(price).toFixed(2)}`;
};

const getStatusColor = (status) => {
    const colors = {
        pending: 'default',
        active: 'processing',
        closed: 'success',
        cancelled: 'error',
        timeout: 'warning'
    };
    return colors[status] || 'default';
};

const getDisplayStatus = (status) => {
    if (status?.toLowerCase() === 'closed') {
        return 'sold';
    }
    return status;
};

const getAuctionStatusDotColor = (status) => {
    const colors = {
        active: '#52c41a', // green
        live: '#52c41a', // green
        completed: '#ff4d4f', // red
        cancelled: '#722ed1', // purple
        scheduled: '#8c8c8c' // gray
    };
    return colors[status] || '#8c8c8c'; // default gray
};

const BrowseLotCard = ({ lot, onViewCompany }) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { currentUser } = useSelector((state) => state.user);

    const isLotFavorite = (lotId) => {
        return currentUser?.favorite_lots?.includes(lotId) || false;
    };

    const toggleFavorite = async (lotId, e) => {
        if (e) {
            e.stopPropagation();
        }
        
        if (!currentUser) {
            message.warning('Please login to add favorites');
            return;
        }

        // Update Redux state
        dispatch(toggleFavoriteLot(lotId));

        // Make the API call in the background
        try {
            await favoriteService.toggleFavoriteLot(lotId);
        } catch (error) {
            // Revert the changes on error
            dispatch(toggleFavoriteLot(lotId));
            message.error('Failed to update favorite status');
        }
    };

    const handleViewCompany = (company, e) => {
        if (e) {
            e.stopPropagation();
        }
        if (onViewCompany) {
            onViewCompany(company);
        }
    };

    return (
        <Card
            key={lot.id}
            hoverable
            style={{
                borderRadius: '16px',
                overflow: 'hidden',
                height: '100%',
                width: '100%',
                minHeight: '100%',
                display: 'flex',
                flexDirection: 'column',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                border: '1px solid #e5e7eb',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
                background: '#ffffff',
                position: 'relative',
                margin: 0
            }}
            onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 8px 24px rgba(0, 0, 0, 0.12)';
                e.currentTarget.style.borderColor = '#d1d5db';
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.06)';
                e.currentTarget.style.borderColor = '#e5e7eb';
            }}
            cover={
                <div style={{ position: 'relative' }}>
                    <div style={{ 
                        position: 'relative',
                        height: 160,
                        background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
                        overflow: 'hidden'
                    }}>
                        {lot.image ? (
                            <img
                                alt={lot.title}
                                src={`${env.BACKEND_STORAGE_URL}/${lot.image}`}
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                    transition: 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)'
                                }}
                                onMouseOver={(e) => e.target.style.transform = 'scale(1.08)'}
                                onMouseOut={(e) => e.target.style.transform = 'scale(1)'}
                                onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.nextSibling.style.display = 'flex';
                                }}
                            />
                        ) : null}
                        <div
                            style={{
                                width: '100%',
                                height: '100%',
                                display: lot.image ? 'none' : 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                                flexDirection: 'column',
                                gap: '16px'
                            }}
                        >
                            <div style={{
                                width: '60px',
                                height: '60px',
                                borderRadius: '50%',
                                background: 'rgba(255, 255, 255, 0.2)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white',
                                fontSize: '28px',
                                fontWeight: 'bold',
                                backdropFilter: 'blur(10px)'
                            }}>
                                M
                            </div>
                            <Text style={{ 
                                fontSize: '14px', 
                                textAlign: 'center',
                                color: 'rgba(255, 255, 255, 0.9)',
                                fontWeight: 500
                            }}>
                                Meveshibids
                            </Text>
                        </div>
                        <div style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: 'linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,0.3) 100%)',
                            opacity: 0,
                            transition: 'opacity 0.4s ease'
                        }} className="hover-overlay" />
                        {/* Auction Status - Left Side (Colored Dot) */}
                        {lot.auction?.status && (
                            <div style={{
                                position: 'absolute',
                                top: 12,
                                left: 12,
                                zIndex: 3,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '24px',
                                height: '24px',
                                borderRadius: '50%',
                                background: 'rgba(255, 255, 255, 0.95)',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                            }}>
                                <div style={{
                                    width: '12px',
                                    height: '12px',
                                    borderRadius: '50%',
                                    backgroundColor: getAuctionStatusDotColor(lot.auction.status),
                                    boxShadow: '0 0 4px rgba(0,0,0,0.2)'
                                }} />
                            </div>
                        )}
                        {/* Featured Badge - Bottom Left */}
                        {lot.is_featured && (
                            <div style={{
                                position: 'absolute',
                                bottom: 8,
                                left: 8,
                                zIndex: 3,
                                background: 'linear-gradient(135deg, #ffd700 0%, #ffed4e 100%)',
                                color: '#1f2937',
                                padding: '2px 8px',
                                borderRadius: '12px',
                                fontSize: '10px',
                                fontWeight: 700,
                                boxShadow: '0 2px 6px rgba(255, 215, 0, 0.4)',
                                border: '1px solid rgba(255, 255, 255, 0.3)',
                                backdropFilter: 'blur(10px)',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px'
                            }}>
                                <StarFilled style={{ fontSize: '10px' }} />
                                Featured
                            </div>
                        )}
                        {/* Lot Status and Favorite Button - Right Side */}
                        <div style={{
                            position: 'absolute',
                            top: 12,
                            right: 12,
                            zIndex: 3,
                            display: 'flex',
                            gap: '8px',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            justifyContent: 'flex-end'
                        }}>
                            <Tag 
                                color={getStatusColor(lot.status)} 
                                style={{ 
                                    padding: '6px 16px',
                                    borderRadius: '20px',
                                    fontWeight: 600,
                                    fontSize: '12px',
                                    border: 'none',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                                }}
                            >
                                {getDisplayStatus(lot.status).toUpperCase()}
                            </Tag>
                            <Button
                                type="text"
                                icon={isLotFavorite(lot.id) ? <HeartFilled style={{ color: '#ff4d4f' }} /> : <HeartOutlined />}
                                onClick={(e) => toggleFavorite(lot.id, e)}
                                style={{
                                    width: '36px',
                                    height: '36px',
                                    borderRadius: '50%',
                                    background: 'rgba(255, 255, 255, 0.95)',
                                    border: 'none',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                    padding: 0
                                }}
                            />
                        </div>
                    </div>
                </div>
            }
            onClick={() => navigate(`/auctions/${lot.auction.slug}/lots/${lot.slug}`)}
            bodyStyle={{ padding: '16px', height: '100%', display: 'flex', flexDirection: 'column' }}
        >
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Space direction="vertical" size={12} style={{ width: '100%' }}>
                    <div>
                        <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '6px', fontWeight: 500 }}>
                            Lot #{lot.lot_number} • {lot.auction.category?.name || 'Category'}
                        </Text>
                        <Title level={4} style={{ margin: 0, fontSize: '16px', lineHeight: 1.4, minHeight: '44px', fontWeight: 700 }}>
                            {lot.title}
                        </Title>
                    </div>
                    
                    {/* Beautiful Price and Info Grid */}
                    <div style={{ 
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: '10px',
                        marginTop: '8px'
                    }}>
                        {/* Price Box */}
                        <div style={{ 
                            background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
                            padding: '10px 12px',
                            borderRadius: '10px',
                            border: '1px solid #bae6fd'
                        }}>
                            <Space size={6}>
                                <DollarCircleOutlined style={{ color: '#0369a1', fontSize: '16px' }} />
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <Text style={{ 
                                        color: '#0c4a6e',
                                        fontSize: '10px',
                                        fontWeight: 600,
                                        display: 'block',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px',
                                        lineHeight: 1.2
                                    }}>
                                        Starting Price
                                    </Text>
                                    <Text style={{ 
                                        color: '#075985',
                                        fontSize: '18px',
                                        fontWeight: 700,
                                        display: 'block',
                                        lineHeight: 1.2
                                    }}>
                                        {formatCurrency(lot.starting_price)}
                                    </Text>
                                </div>
                            </Space>
                        </div>

                        {/* Quantity Box */}
                        <div style={{ 
                            background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                            padding: '10px 12px',
                            borderRadius: '10px',
                            border: '1px solid #fcd34d'
                        }}>
                            <Space size={6}>
                                <ShoppingCartOutlined style={{ color: '#d97706', fontSize: '16px' }} />
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <Text style={{ 
                                        color: '#92400e',
                                        fontSize: '10px',
                                        fontWeight: 600,
                                        display: 'block',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px',
                                        lineHeight: 1.2
                                    }}>
                                        Quantity
                                    </Text>
                                    <Text style={{ 
                                        color: '#b45309',
                                        fontSize: '18px',
                                        fontWeight: 700,
                                        display: 'block',
                                        lineHeight: 1.2
                                    }}>
                                        {lot.quantity}
                                    </Text>
                                </div>
                            </Space>
                        </div>
                    </div>

                    {lot.bids_count > 0 && (
                        <div style={{ marginTop: '10px' }}>
                            <Tag color="blue" style={{ fontSize: '12px', margin: 0, padding: '4px 12px', fontWeight: 600, borderRadius: '6px' }}>{lot.bids_count} bids</Tag>
                        </div>
                    )}

                    {/* Action Buttons */}
                    <div style={{ 
                        marginTop: '12px',
                        display: 'flex',
                        gap: '8px'
                    }}>
                        <Button
                            type="default"
                            icon={<EyeOutlined />}
                            onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/auctions/${lot.auction.slug}`);
                            }}
                            style={{
                                flex: 1,
                                height: '36px',
                                borderRadius: '8px',
                                fontWeight: 500,
                                border: '1px solid #d9d9d9'
                            }}
                        >
                            Auction Details
                        </Button>
                        <Button
                            type="primary"
                            icon={<FileTextOutlined />}
                            onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/auctions/${lot.auction.slug}/lots/${lot.slug}`);
                            }}
                            className="lot-details-button"
                            style={{
                                flex: 1,
                                height: '36px',
                                borderRadius: '8px',
                                fontWeight: 500,
                                background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                                border: 'none',
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                boxShadow: '0 2px 8px rgba(24, 144, 255, 0.3)',
                                position: 'relative',
                                overflow: 'hidden'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'translateY(-2px) scale(1.02)';
                                e.currentTarget.style.boxShadow = '0 6px 16px rgba(24, 144, 255, 0.4)';
                                e.currentTarget.style.background = 'linear-gradient(135deg, #40a9ff 0%, #1890ff 100%)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'translateY(0) scale(1)';
                                e.currentTarget.style.boxShadow = '0 2px 8px rgba(24, 144, 255, 0.3)';
                                e.currentTarget.style.background = 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)';
                            }}
                        >
                            Lot Details
                        </Button>
                    </div>

                    <Divider style={{ margin: '12px 0' }} />

                    {/* Company Information Section - Matching AuctionCard Style */}
                    <div 
                        style={{
                            padding: '10px 12px',
                            background: lot.company 
                                ? 'linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%)'
                                : 'linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%)',
                            borderRadius: '8px',
                            border: '1px solid #e5e7eb',
                            cursor: lot.company ? 'pointer' : 'default',
                            transition: 'all 0.2s ease'
                        }}
                        onClick={(e) => {
                            if (lot.company) {
                                handleViewCompany(lot.company, e);
                            }
                        }}
                        onMouseEnter={(e) => {
                            if (lot.company) {
                                e.currentTarget.style.background = 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)';
                                e.currentTarget.style.borderColor = '#93c5fd';
                            }
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%)';
                            e.currentTarget.style.borderColor = '#e5e7eb';
                        }}
                    >
                        <Space size={10} style={{ width: '100%' }}>
                            {lot.company ? (
                                <>
                                    {lot.company.company_logo_url ? (
                                        <Avatar
                                            src={lot.company.company_logo_url.startsWith('http') 
                                                ? lot.company.company_logo_url 
                                                : env.BACKEND_URL + lot.company.company_logo_url}
                                            alt={lot.company.company_name}
                                            size={32}
                                            style={{
                                                border: '2px solid #d1d5db',
                                                flexShrink: 0
                                            }}
                                        />
                                    ) : (
                                        <Avatar
                                            size={32}
                                            style={{
                                                background: 'linear-gradient(135deg, #1890ff, #52c41a)',
                                                color: 'white',
                                                fontWeight: 'bold',
                                                fontSize: '14px',
                                                border: '2px solid #d1d5db',
                                                flexShrink: 0
                                            }}
                                        >
                                            {lot.company.company_name?.charAt(0).toUpperCase() || 'C'}
                                        </Avatar>
                                    )}
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <Text strong style={{ 
                                            fontSize: '12px',
                                            color: '#1f2937',
                                            display: 'block',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            lineHeight: 1.3,
                                            fontWeight: 600
                                        }}>
                                            {lot.company.company_name || 'Company'}
                                        </Text>
                                        {lot.company.short_tagline && (
                                            <Text type="secondary" style={{ 
                                                fontSize: '10px',
                                                display: 'block',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                                lineHeight: 1.2,
                                                marginTop: '2px'
                                            }}>
                                                {lot.company.short_tagline}
                                            </Text>
                                        )}
                                    </div>
                                    <ShopOutlined style={{ 
                                        color: '#9ca3af',
                                        fontSize: '14px',
                                        flexShrink: 0
                                    }} />
                                </>
                            ) : (
                                <>
                                    <Avatar
                                        size={32}
                                        icon={<UserOutlined />}
                                        style={{
                                            background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                                            color: 'white',
                                            border: '2px solid #d1d5db',
                                            flexShrink: 0
                                        }}
                                    />
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <Text strong style={{ 
                                            fontSize: '12px',
                                            color: '#1f2937',
                                            display: 'block',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            lineHeight: 1.3,
                                            fontWeight: 600
                                        }}>
                                            Individual
                                        </Text>
                                    </div>
                                    <UserOutlined style={{ 
                                        color: '#9ca3af',
                                        fontSize: '14px',
                                        flexShrink: 0
                                    }} />
                                </>
                            )}
                        </Space>
                    </div>

                    {/* Prominent Auction Name */}
                    <div style={{
                        marginTop: '8px',
                        padding: '12px',
                        background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
                        borderRadius: '10px',
                        border: '1px solid #bae6fd'
                    }}>
                        <Space size={6} style={{ width: '100%', minWidth: 0 }}>
                            <ShopOutlined style={{ color: '#0369a1', fontSize: '16px', flexShrink: 0 }} />
                            <Text strong style={{ 
                                fontSize: '14px',
                                color: '#0c4a6e',
                                display: 'block',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                fontWeight: 600,
                                lineHeight: 1.4,
                                flex: 1,
                                minWidth: 0
                            }}>
                                {lot.auction.title && lot.auction.title.length > 50 
                                    ? `${lot.auction.title.substring(0, 50)}...` 
                                    : lot.auction.title}
                            </Text>
                        </Space>
                    </div>
                </Space>
            </div>
        </Card>
    );
};

export default BrowseLotCard;

